import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Link2, Unlink, FileText, Users, CheckCircle, XCircle, Settings, Save, Eye, EyeOff, Pause, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminXero() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Credentials form
  const [creds, setCreds] = useState({ client_id: '', client_secret: '', redirect_uri: '' });
  const [credsLoading, setCredsLoading] = useState(true);
  const [credsSaving, setCredsSaving] = useState(false);
  const [hasSecret, setHasSecret] = useState(false);
  const [maskedSecret, setMaskedSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [secretEdited, setSecretEdited] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [syncToggleLoading, setSyncToggleLoading] = useState(false);

  useEffect(() => {
    fetchStatus();
    fetchCredentials();
    fetchSyncToggle();
    if (searchParams.get('connected') === 'true') {
      toast.success(`Connected to Xero: ${searchParams.get('org') || 'Organisation'}`);
    }
    if (searchParams.get('error')) {
      toast.error(`Xero connection failed: ${searchParams.get('error')}`);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/xero/status`);
      setStatus(res.data);
    } catch { setStatus({ connected: false }); }
    finally { setLoading(false); }
  };

  const fetchCredentials = async () => {
    try {
      const res = await axios.get(`${API}/admin/xero/settings`);
      setCreds({
        client_id: res.data.client_id || '',
        client_secret: '',
        redirect_uri: res.data.redirect_uri || '',
      });
      setHasSecret(res.data.has_secret);
      setMaskedSecret(res.data.client_secret_masked || '');
    } catch { /* credentials not set yet */ }
    finally { setCredsLoading(false); }
  };

  const handleSaveCredentials = async () => {
    if (!creds.client_id.trim()) { toast.error('Client ID is required'); return; }
    if (!creds.redirect_uri.trim()) { toast.error('Redirect URI is required'); return; }
    if (!hasSecret && !creds.client_secret.trim()) { toast.error('Client Secret is required'); return; }

    setCredsSaving(true);
    try {
      await axios.put(`${API}/admin/xero/settings`, {
        client_id: creds.client_id.trim(),
        client_secret: secretEdited ? creds.client_secret.trim() : '',
        redirect_uri: creds.redirect_uri.trim(),
      });
      toast.success('Xero credentials saved');
      setSecretEdited(false);
      fetchCredentials();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credentials');
    } finally { setCredsSaving(false); }
  };

  const handleConnect = async () => {
    try {
      const res = await axios.get(`${API}/xero/auth-url`);
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error('Failed to generate Xero auth URL. Make sure credentials are saved first.');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect from Xero? Existing synced data will remain.')) return;
    try {
      await axios.delete(`${API}/xero/disconnect`);
      toast.success('Disconnected from Xero');
      setStatus({ connected: false });
    } catch { toast.error('Failed to disconnect'); }
  };

  const fetchSyncToggle = async () => {
    try {
      const res = await axios.get(`${API}/admin/xero/sync-toggle`);
      setSyncEnabled(res.data.enabled);
    } catch { /* default to true */ }
  };

  const handleSyncToggle = async (enabled) => {
    setSyncToggleLoading(true);
    setSyncEnabled(enabled);
    try {
      await axios.put(`${API}/admin/xero/sync-toggle`, { enabled });
      toast.success(enabled ? 'Xero auto-sync enabled' : 'Xero auto-sync paused');
    } catch {
      setSyncEnabled(!enabled);
      toast.error('Failed to update sync toggle');
    } finally {
      setSyncToggleLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-xero" className="space-y-6">
        <div>
          <h1 className="text-2xl font-black text-white uppercase tracking-tight">Xero Integration</h1>
          <p className="text-zinc-400 mt-1 text-sm">Connect to Xero to sync orders, invoices, and customers</p>
        </div>

        {/* API Credentials */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="h-5 w-5 text-[#78BE20]" />
            <h2 className="text-white font-medium">API Credentials</h2>
          </div>
          <p className="text-zinc-500 text-xs mb-4">
            Enter your Xero app credentials. Get them from{' '}
            <a href="https://developer.xero.com/app/manage" target="_blank" rel="noopener noreferrer" className="text-[#13B5EA] hover:underline">
              developer.xero.com/app/manage
            </a>
          </p>

          {credsLoading ? (
            <div className="text-zinc-500 text-sm py-4">Loading credentials...</div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-400">Client ID</Label>
                <Input
                  value={creds.client_id}
                  onChange={(e) => setCreds({ ...creds, client_id: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700 font-mono text-sm"
                  placeholder="Your Xero Client ID"
                  data-testid="xero-client-id"
                />
              </div>

              <div>
                <Label className="text-zinc-400">Client Secret</Label>
                <div className="relative mt-1">
                  {!secretEdited && hasSecret ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={maskedSecret}
                        disabled
                        className="bg-zinc-800 border-zinc-700 font-mono text-sm text-zinc-500 flex-1"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-400"
                        onClick={() => setSecretEdited(true)}
                        data-testid="xero-edit-secret-btn"
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Input
                        type={showSecret ? "text" : "password"}
                        value={creds.client_secret}
                        onChange={(e) => { setCreds({ ...creds, client_secret: e.target.value }); setSecretEdited(true); }}
                        className="bg-zinc-800 border-zinc-700 font-mono text-sm pr-10"
                        placeholder={hasSecret ? "Enter new secret (leave blank to keep existing)" : "Your Xero Client Secret"}
                        data-testid="xero-client-secret"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecret(!showSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      >
                        {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-zinc-400">Redirect URI</Label>
                <Input
                  value={creds.redirect_uri}
                  onChange={(e) => setCreds({ ...creds, redirect_uri: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700 font-mono text-sm"
                  placeholder="https://your-domain.com/api/xero/callback"
                  data-testid="xero-redirect-uri"
                />
                <p className="text-zinc-600 text-xs mt-1">Must match the redirect URI configured in your Xero app</p>
              </div>

              <Button
                onClick={handleSaveCredentials}
                disabled={credsSaving}
                className="bg-[#78BE20] text-black hover:bg-[#65a31b]"
                data-testid="xero-save-credentials-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {credsSaving ? 'Saving...' : 'Save Credentials'}
              </Button>
            </div>
          )}
        </div>

        {/* Connection Status */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${status?.connected ? 'bg-[#78BE20]/20' : 'bg-zinc-800'}`}>
                {status?.connected ? <CheckCircle className="h-6 w-6 text-[#78BE20]" /> : <XCircle className="h-6 w-6 text-zinc-500" />}
              </div>
              <div>
                <h2 className="text-white font-medium">
                  {loading ? 'Checking connection...' : status?.connected ? 'Connected to Xero' : 'Not Connected'}
                </h2>
                {status?.connected && (
                  <p className="text-zinc-400 text-sm">
                    Organisation: <span className="text-white">{status.org_name}</span>
                    {status.connected_at && (
                      <span className="text-zinc-600 ml-2">
                        · Connected {new Date(status.connected_at).toLocaleDateString('en-AU')}
                      </span>
                    )}
                  </p>
                )}
                {!status?.connected && !loading && (
                  <p className="text-zinc-500 text-sm">Save credentials above, then click Connect to authorise with Xero</p>
                )}
              </div>
            </div>
            <div>
              {status?.connected ? (
                <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={handleDisconnect}>
                  <Unlink className="h-4 w-4 mr-2" /> Disconnect
                </Button>
              ) : (
                <Button className="bg-[#13B5EA] hover:bg-[#0fa0d0] text-white" onClick={handleConnect} disabled={loading} data-testid="connect-xero-btn">
                  <Link2 className="h-4 w-4 mr-2" /> Connect to Xero
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Sync Toggle */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${syncEnabled ? 'bg-[#78BE20]/20' : 'bg-amber-500/20'}`}>
                {syncEnabled ? <Play className="h-6 w-6 text-[#78BE20]" /> : <Pause className="h-6 w-6 text-amber-500" />}
              </div>
              <div>
                <h2 className="text-white font-medium">
                  Auto-Sync {syncEnabled ? 'Active' : 'Paused'}
                </h2>
                <p className="text-zinc-400 text-sm">
                  {syncEnabled
                    ? 'Orders, invoices, and payments are automatically synced to Xero'
                    : 'Auto-sync is paused. Orders will NOT be sent to Xero. Use this while testing.'}
                </p>
              </div>
            </div>
            <Switch
              checked={syncEnabled}
              onCheckedChange={handleSyncToggle}
              disabled={syncToggleLoading}
              data-testid="xero-sync-toggle"
            />
          </div>
          {!syncEnabled && (
            <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-md">
              <p className="text-amber-400 text-xs">
                Automatic syncing to Xero is currently disabled. Manual sync buttons on individual orders and invoices will also be blocked.
                Re-enable when you're ready to go live.
              </p>
            </div>
          )}
        </div>

        {status?.connected && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="h-5 w-5 text-[#78BE20]" />
                  <h3 className="text-white font-medium">Orders to Invoices</h3>
                </div>
                <p className="text-zinc-400 text-sm mb-3">
                  Sync orders from your store to Xero as draft invoices. Use the "Sync to Xero" button on any order.
                </p>
                <Badge className="bg-[#78BE20]/20 text-[#78BE20]">Active</Badge>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="h-5 w-5 text-[#78BE20]" />
                  <h3 className="text-white font-medium">Customers to Contacts</h3>
                </div>
                <p className="text-zinc-400 text-sm mb-3">
                  Sync customer details to Xero contacts. Use the "Sync to Xero" button on customer profiles.
                </p>
                <Badge className="bg-[#78BE20]/20 text-[#78BE20]">Active</Badge>
              </div>
            </div>

            {/* How to Use */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
              <h3 className="text-white font-medium mb-3">How to sync</h3>
              <div className="space-y-2 text-sm text-zinc-400">
                <p>1. Go to <strong className="text-white">Orders</strong> and open an order detail</p>
                <p>2. Click <strong className="text-white">"Sync to Xero"</strong> to create a draft invoice in Xero</p>
                <p>3. The invoice will appear in your Xero dashboard as a draft for review</p>
                <p>4. Customers are auto-created as Xero contacts when syncing orders</p>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
